// Controlled recurrence-rule form block. Collapsed by default; expanding
// emits a RecurrenceRule with sensible defaults. The Frequency + Interval
// pair is rendered as a sentence ("Repeat every {N} {unit}") with the unit
// label switching between singular and plural based on N. When endDate is
// blank, surfaces the 104-occurrence safety cap.

import { useId } from "react";
import type { RecurrenceFrequency, RecurrenceRule } from "../../../../types";
import { frequencyUnitLabel } from "./frequencyUnitLabel";
import RecurrenceIntervalInput from "./RecurrenceIntervalInput";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";

const FREQUENCIES: RecurrenceFrequency[] = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "annually",
];

interface RecurrenceRuleInputProps {
  value: RecurrenceRule | undefined;
  onChange: (rule: RecurrenceRule | undefined) => void;
  disabled?: boolean;
  /** Effective end date of the parent contract (admin-maintained). When
   *  present and the end date field is blank, replaces the 104-cap
   *  helper text with the contract end date as the default boundary. */
  contractEndDate?: string;
}

export default function RecurrenceRuleInput({
  value,
  onChange,
  disabled,
  contractEndDate,
}: RecurrenceRuleInputProps) {
  const id = useId();
  const active = !!value;

  const handleToggle = () => {
    if (active) {
      onChange(undefined);
    } else {
      onChange({
        frequency: "monthly",
        interval: 1,
        startDate: getBrisbaneToday(),
      });
    }
  };

  const patch = (update: Partial<RecurrenceRule>) => {
    if (!value) return;
    onChange({ ...value, ...update });
  };

  return (
    <fieldset className="space-y-2">
      <label className="inline-flex items-center gap-2">
        <input
          type="checkbox"
          checked={active}
          onChange={handleToggle}
          disabled={disabled}
          className="accent-brand-600"
        />
        <span className="text-caption font-medium text-slate-700 dark:text-slate-300">
          Recurring
        </span>
      </label>

      {active && value && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-caption text-slate-700 dark:text-slate-300">
              Repeat every
            </span>
            <div>
              <label htmlFor={`${id}-interval`} className="sr-only">
                Repeat every
              </label>
              <RecurrenceIntervalInput
                id={`${id}-interval`}
                value={value.interval ?? 1}
                onCommit={(interval) => patch({ interval })}
                disabled={disabled}
              />
            </div>
            <div>
              <label htmlFor={`${id}-unit`} className="sr-only">
                Unit
              </label>
              <select
                id={`${id}-unit`}
                className="input"
                value={value.frequency}
                onChange={(e) =>
                  patch({ frequency: e.target.value as RecurrenceFrequency })
                }
                disabled={disabled}
              >
                {FREQUENCIES.map((f) => (
                  <option key={f} value={f}>
                    {frequencyUnitLabel(f, value.interval ?? 1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label htmlFor={`${id}-start`} className="label">
                Start Date
              </label>
              <input
                id={`${id}-start`}
                type="date"
                className="input"
                value={value.startDate}
                onChange={(e) => patch({ startDate: e.target.value })}
                disabled={disabled}
              />
            </div>

            <div>
              <label htmlFor={`${id}-end`} className="label">
                End Date
              </label>
              <input
                id={`${id}-end`}
                type="date"
                className="input"
                value={value.endDate ?? ""}
                onChange={(e) =>
                  patch({ endDate: e.target.value || undefined })
                }
                disabled={disabled}
              />
              {!value.endDate && (
                <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
                  {contractEndDate
                    ? `If blank, defaults to contract end date: ${formatDayMonthYear(contractEndDate)}.`
                    : "If omitted, generation caps at 104 occurrences."}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </fieldset>
  );
}
